<template>
  <section class="conversation-chat">
    <header v-if="conversation" class="conversation-chat-header">
      <div class="conversation-chat-header-main">
        <div
          class="conversation-chat-avatar"
          :style="{ background: conversation.avatarColor || '#0066FF' }"
        >
          <img
            v-if="conversation.type === 'personal' && conversation.avatarUrl"
            :src="conversation.avatarUrl"
            :alt="conversation.title || ''"
          />
          <span v-else-if="conversation.type === 'ticket'">#</span>
          <v-icon v-else-if="conversation.type === 'group'" dark small>
            mdi-account-group
          </v-icon>
          <v-icon v-else-if="conversation.type === 'channel'" dark small>
            mdi-bullhorn
          </v-icon>
          <span v-else>{{ titleInitial(conversation.title) }}</span>
        </div>
        <div class="conversation-chat-header-text">
          <div class="conversation-chat-title">
            <template v-if="conversation.type === 'ticket'">
              #{{ toPersianDigits(conversation.ticketId) }}
            </template>
            <template v-else>{{ conversation.title }}</template>
            <span
              v-if="conversation.state === 'close'"
              class="conversation-chat-state is-closed"
            >
              {{ $t("closed") }}
            </span>
          </div>
          <div class="conversation-chat-subtitle">
            <template v-if="conversation.type === 'personal'">
              {{
                conversation.online
                  ? $t("online")
                  : $t("conversation-last-seen-recently")
              }}
            </template>
            <template
              v-else-if="
                conversation.type === 'group' || conversation.type === 'channel'
              "
            >
              {{
                $t("conversation-members", {
                  count: toPersianDigits(
                    membersCount || conversation.members || 0
                  ),
                })
              }}
            </template>
            <template v-else>
              {{ $t("conversation-ticket-chat") }}
              <span v-if="membersCount">
                ·
                {{
                  $t("conversation-members", {
                    count: toPersianDigits(membersCount),
                  })
                }}
              </span>
            </template>
          </div>
        </div>
      </div>
      <div class="conversation-chat-actions">
        <div
          v-if="!pending && showMembersButton"
          class="conversation-chat-members-wrap"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="conversation-chat-action"
                data-members-toggle
                v-bind="attrs"
                v-on="on"
                :class="{ 'is-active': membersOpen }"
                @click.stop="$emit('toggle-members')"
              >
                <v-icon size="20" :color="membersOpen ? '#0066FF' : '#555'">
                  mdi-account-multiple-outline
                </v-icon>
              </button>
            </template>
            <span>{{ $t("conversation-members-title") }}</span>
          </v-tooltip>
          <slot name="members" />
        </div>
        <v-menu
          v-if="showHeaderMoreMenu"
          offset-y
          left
          :close-on-content-click="true"
          content-class="conversation-chat-header-menu"
        >
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              class="conversation-chat-action"
              v-bind="attrs"
              v-on="on"
              :disabled="stateBusy"
            >
              <v-icon size="20" color="#555">mdi-dots-vertical</v-icon>
            </button>
          </template>
          <v-list dense class="conversation-chat-header-menu-list">
            <v-list-item
              v-if="canCloseGroup && conversation.state === 'close'"
              :disabled="stateBusy"
              @click="$emit('reopen')"
            >
              <v-list-item-icon class="conversation-chat-header-menu-icon">
                <v-icon size="20" color="#0066FF">mdi-lock-open-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ $t("conversation-reopen") }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-else-if="canCloseGroup"
              :disabled="stateBusy"
              @click="$emit('close-chat')"
            >
              <v-list-item-icon class="conversation-chat-header-menu-icon">
                <v-icon size="20" color="#555">mdi-lock-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ $t("close") }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="canDeleteChat"
              :disabled="stateBusy"
              class="conversation-chat-header-menu-danger"
              @click="$emit('delete-chat')"
            >
              <v-list-item-icon class="conversation-chat-header-menu-icon">
                <v-icon size="20" color="#ef4444">mdi-trash-can-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ deleteChatMenuLabel }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="canLeaveGroup"
              :disabled="stateBusy"
              class="conversation-chat-header-menu-danger"
              @click="$emit('leave-group')"
            >
              <v-list-item-icon class="conversation-chat-header-menu-icon">
                <v-icon size="20" color="#ef4444">mdi-exit-to-app</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ $t("conversation-leave-group") }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </header>

    <div v-else class="conversation-chat-empty">
      <v-progress-circular
        v-if="loading"
        indeterminate
        color="#0066FF"
        size="28"
        width="2"
      />
      <template v-else>
        <v-icon large color="#c5c5c5">mdi-chat-outline</v-icon>
        <p>{{ $t("choose a message") }}</p>
      </template>
    </div>

    <div v-if="conversation" class="conversation-chat-body">
      <div ref="messagesEl" class="conversation-chat-messages">
        <div
          v-for="(msg, msgIndex) in messages"
          :key="msg.id"
          class="conversation-chat-row"
          :data-message-id="msg.id"
          :class="{
            'is-mine': msg.mine,
            'is-system': msg.system,
            'is-highlighted': highlightedMessageId === msg.id,
          }"
        >
          <div v-if="msg.system" class="conversation-chat-system">
            {{ msg.text }}
          </div>
          <template v-else>
            <div
              v-if="!msg.mine && showPeerAvatars"
              class="conversation-chat-msg-avatar-wrap"
            >
              <div
                v-if="shouldShowPeerAvatar(msg, msgIndex)"
                class="conversation-chat-msg-avatar"
                :style="{ background: msg.avatarColor || '#3b82f6' }"
              >
                <img
                  v-if="msg.avatarUrl"
                  :src="msg.avatarUrl"
                  :alt="msg.sender || ''"
                />
                <span v-else>{{ displaySender(msg) ? initialsOf(displaySender(msg)) : (msg.initials || "?") }}</span>
              </div>
            </div>
            <div
              class="conversation-chat-bubble"
              @contextmenu.prevent.stop="openMessageMenu(msg, $event)"
            >
            <button
              v-if="!msg.mine && showSender"
              type="button"
              class="conversation-chat-sender is-clickable"
              :disabled="msg.userId == null"
              @click.stop="onSenderClick(msg)"
            >
              {{ displaySender(msg) }}
            </button>

            <div
              v-if="msg.forwardedFrom"
              class="conversation-chat-forwarded"
            >
              <div class="conversation-chat-forwarded-label">
                {{ label("forwardedFrom", "Forwarded from") }}
              </div>
              <button
                v-if="msg.forwardedFrom.sender"
                type="button"
                class="conversation-chat-forwarded-sender"
                :class="{ 'is-clickable': msg.forwardedFrom.userId != null }"
                :disabled="msg.forwardedFrom.userId == null"
                @click.stop="onForwardedSenderClick(msg.forwardedFrom)"
              >
                <span class="conversation-chat-forwarded-avatar">
                  {{ initialsOf(msg.forwardedFrom.sender) }}
                </span>
                <span class="conversation-chat-forwarded-name">
                  {{ msg.forwardedFrom.sender }}
                </span>
              </button>
            </div>

            <div
              v-if="msg.replyTo"
              class="conversation-chat-quote"
              :class="{
                'is-deleted': msg.replyTo.deleted,
                'is-clickable': msg.replyTo.id != null && !msg.replyTo.deleted,
              }"
              @click.stop="scrollToReplyTarget(msg.replyTo)"
            >
              <div v-if="msg.replyTo.sender" class="conversation-chat-quote-sender">
                {{ msg.replyTo.sender }}
              </div>
              <div class="conversation-chat-quote-text">
                {{
                  msg.replyTo.deleted
                    ? label("deleted", "Deleted")
                    : msg.replyTo.text || "…"
                }}
              </div>
            </div>

            <div
              v-if="msg.attachments && msg.attachments.length"
              class="conversation-chat-msg-files"
            >
              <div
                v-for="(file, fIndex) in msg.attachments"
                :key="file.id || fIndex"
                class="conversation-chat-msg-file"
              >
                <a
                  v-if="isImage(file.type) || isImageExt(file.extension || file.name)"
                  :href="file.preview"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="conversation-chat-msg-img-link"
                  @click.stop
                >
                  <img
                    :src="file.preview"
                    class="conversation-chat-msg-img"
                    :alt="file.name || ''"
                  />
                </a>
                <a
                  v-else-if="file.preview"
                  :href="file.preview"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="conversation-chat-msg-doc"
                  @click.stop
                >
                  <v-icon small :color="msg.mine ? '#3b82f6' : '#0066FF'">
                    mdi-file-document-outline
                  </v-icon>
                  <span>{{ file.name }}</span>
                </a>
                <div v-else class="conversation-chat-msg-doc">
                  <v-icon small :color="msg.mine ? '#3b82f6' : '#0066FF'">
                    mdi-file-document-outline
                  </v-icon>
                  <span>{{ file.name }}</span>
                </div>
              </div>
            </div>

            <div v-if="msg.text" class="conversation-chat-text">
              <template v-for="(part, pIndex) in messageParts(msg)">
                <button
                  v-if="part.type === 'mention'"
                  :key="'m-' + msg.id + '-' + pIndex"
                  type="button"
                  class="conversation-chat-mention-link"
                  @click.stop="onMentionClick(part)"
                >
                  {{ part.text }}
                </button>
                <span
                  v-else
                  :key="'t-' + msg.id + '-' + pIndex"
                >{{ part.text }}</span>
              </template>
            </div>

            <div class="conversation-chat-meta">
              <span class="conversation-chat-time">{{ msg.time }}</span>
              <span
                v-if="msg.mine && msg.pending"
                class="conversation-chat-checks is-pending"
              >
                <v-icon x-small color="#9aa0a6">mdi-clock-outline</v-icon>
              </span>
              <span
                v-else-if="msg.mine && msg.failed"
                class="conversation-chat-checks is-failed"
              >
                <v-icon x-small color="#ef4444">mdi-alert-circle-outline</v-icon>
              </span>
              <v-tooltip
                v-else-if="msg.mine && messageReaders(msg).length"
                bottom
              >
                <template v-slot:activator="{ on, attrs }">
                  <span
                    class="conversation-chat-checks"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon x-small color="#3b82f6">mdi-check-all</v-icon>
                  </span>
                </template>
                <div class="conversation-chat-readers-tip">
                  <div
                    v-for="reader in messageReaders(msg)"
                    :key="'reader-' + msg.id + '-' + reader.userId"
                    class="conversation-chat-readers-tip-row"
                  >
                    <div
                      class="conversation-chat-readers-tip-avatar"
                      :style="{ background: reader.avatarColor || '#3b82f6' }"
                    >
                      <img
                        v-if="reader.avatarUrl"
                        :src="reader.avatarUrl"
                        :alt="reader.name || ''"
                      />
                      <span v-else>{{ reader.initials || "?" }}</span>
                    </div>
                    <span class="conversation-chat-readers-tip-name">
                      {{ reader.name }}
                    </span>
                  </div>
                </div>
              </v-tooltip>
              <span
                v-else-if="msg.mine && Number(msg.readersCount) > 0"
                class="conversation-chat-checks"
              >
                <v-icon x-small color="#3b82f6">mdi-check-all</v-icon>
              </span>
              <span
                v-else-if="msg.mine"
                class="conversation-chat-checks"
              >
                <v-icon x-small color="#3b82f6">mdi-check</v-icon>
              </span>
              <span v-if="msg.isEdited" class="conversation-chat-edited">
                {{ label("edited", "edited") }}
              </span>
            </div>
          </div>
          </template>
        </div>
        <div v-if="loading" class="conversation-chat-loading">
          <v-progress-circular indeterminate color="#0066FF" size="28" width="2" />
        </div>
      </div>
    </div>

    <div
      v-if="forwardOpen"
      class="conversation-chat-forward-overlay"
      @click.self="cancelForward"
    >
      <div class="conversation-chat-forward-panel" @click.stop>
        <header class="conversation-chat-forward-head">
          <span>{{ label("forward", "Forward") }}</span>
          <button type="button" @click="cancelForward">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </header>
        <div v-if="!forwardTargets.length" class="conversation-chat-forward-empty">
          {{ $t("no-data-found") }}
        </div>
        <button
          v-for="target in forwardTargets"
          :key="target.id"
          type="button"
          class="conversation-chat-forward-item"
          @click="pickForwardTarget(target)"
        >
          <div
            class="conversation-chat-forward-avatar"
            :style="{ background: '#0066FF' }"
          >
            <span v-if="target.type === 'ticket'">#</span>
            <v-icon v-else-if="target.type === 'group'" dark x-small>
              mdi-account-group
            </v-icon>
            <v-icon v-else-if="target.type === 'channel'" dark x-small>
              mdi-bullhorn
            </v-icon>
            <span v-else>{{ titleInitial(target.title) }}</span>
          </div>
          <div class="conversation-chat-forward-meta">
            <div class="conversation-chat-forward-title">
              <template v-if="target.type === 'ticket' && target.ticketId != null">
                #{{ toPersianDigits(target.ticketId) }}
              </template>
              <template v-else>{{ target.title }}</template>
            </div>
            <div class="conversation-chat-forward-type">{{ target.type }}</div>
          </div>
        </button>
      </div>
    </div>

    <div v-if="conversation" class="conversation-chat-footer">
      <div v-if="disabled" class="conversation-chat-closed">
        <span>{{ $t("closed") }}</span>
        <button
          v-if="canCloseGroup"
          type="button"
          class="conversation-chat-reopen-btn"
          :disabled="stateBusy"
          @click="$emit('reopen')"
        >
          {{ $t("conversation-reopen") }}
        </button>
      </div>
      <div
        v-if="fileSizeError"
        class="conversation-chat-file-error"
      >
        <v-icon size="18" color="#b45309">mdi-alert-circle-outline</v-icon>
        <span class="conversation-chat-file-error-text">{{ fileSizeError }}</span>
        <button type="button" @click="clearFileSizeError">
          <v-icon size="16" color="#92400e">mdi-close</v-icon>
        </button>
      </div>
      <div
        v-if="!disabled && !composerHidden && filePreviews.length"
        class="conversation-chat-attachments"
      >
        <div
          v-for="(file, index) in filePreviews"
          :key="index"
          class="conversation-chat-attachment"
        >
          <img
            v-if="isImage(file.type)"
            :src="file.preview"
            class="conversation-chat-attachment-img"
            alt=""
          />
          <div v-else class="conversation-chat-attachment-doc">
            <v-icon small color="#0066FF">mdi-file-document-outline</v-icon>
            <span>{{ file.name }}</span>
          </div>
          <button
            type="button"
            class="conversation-chat-attachment-remove"
            @click="removeFile(index)"
          >
            <v-icon x-small color="#fff">mdi-close</v-icon>
          </button>
        </div>
      </div>

      <footer v-if="!disabled && !composerHidden" class="conversation-chat-composer">
        <div
          v-if="replyToMessage"
          class="conversation-chat-reply-bar"
        >
          <div class="conversation-chat-reply-bar-body">
            <div class="conversation-chat-reply-bar-label">
              {{ label("reply", "Reply") }}
              <span v-if="replyToMessage.sender">
                · {{ replyToMessage.sender }}
              </span>
            </div>
            <div class="conversation-chat-reply-bar-text">
              {{ replyToMessage.text || "…" }}
            </div>
          </div>
          <button type="button" @click="cancelReply">
            <v-icon size="18" color="#555">mdi-close</v-icon>
          </button>
        </div>

        <div
          v-if="editingMessageId != null"
          class="conversation-chat-reply-bar is-edit"
        >
          <div class="conversation-chat-reply-bar-body">
            <div class="conversation-chat-reply-bar-label">
              {{ $t("edit") }}
            </div>
          </div>
          <button type="button" @click="cancelEdit">
            <v-icon size="18" color="#555">mdi-close</v-icon>
          </button>
        </div>

        <input
          ref="fileInput"
          type="file"
          multiple
          hidden
          @change="onAttach"
        />
        <div class="conversation-chat-input-shell">
          <button
            type="button"
            class="conversation-chat-attach"
            :disabled="sending"
            @click="triggerFileInput"
          >
            <v-icon size="20" color="#555555">mdi-paperclip</v-icon>
          </button>
          <div class="conversation-chat-input-wrap">
            <textarea
              ref="draftInput"
              v-model="draft"
              class="conversation-chat-input"
              rows="1"
              :placeholder="$t('write the message')"
              :disabled="sending"
              @keydown.enter.exact.prevent="send"
              @keydown.down.exact.prevent="mentionMove(1)"
              @keydown.up.exact.prevent="mentionMove(-1)"
              @keydown.esc.exact="closeMention"
              @input="onDraftInput"
              @paste="handlePaste"
            />
            <div
              v-if="mentionOpen && mentionCandidates.length"
              class="conversation-chat-mention-dropdown"
            >
              <button
                v-for="(item, index) in mentionCandidates"
                :key="memberUserId(item) || index"
                type="button"
                class="conversation-chat-mention-item"
                :class="{ 'is-active': index === mentionIndex }"
                @mousedown.prevent="selectMention(item)"
              >
                <span
                  class="conversation-chat-mention-avatar"
                  :style="{ background: memberAvatarColor(item) }"
                >
                  <img
                    v-if="memberAvatarUrl(item)"
                    :src="memberAvatarUrl(item)"
                    :alt="memberDisplayName(item)"
                  />
                  <span v-else>{{
                    initialsOf(memberDisplayName(item))
                  }}</span>
                </span>
                <span class="conversation-chat-mention-name">{{
                  memberDisplayName(item)
                }}</span>
              </button>
            </div>
          </div>
          <button
            type="button"
            class="conversation-chat-send"
            :class="{ 'is-ready': canSend }"
            :disabled="!canSend"
            @click="send"
          >
            <v-progress-circular
              v-if="sending"
              indeterminate
              color="#ffffff"
              size="16"
              width="2"
            />
            <v-icon v-else size="18" color="#ffffff">
              {{ editingMessageId != null ? "mdi-check" : "mdi-send" }}
            </v-icon>
          </button>
        </div>
      </footer>
    </div>

    <div
      v-if="contextMenuMessage"
      ref="contextMenu"
      class="conversation-chat-context-menu"
      :style="contextMenuStyle"
      @mousedown.stop
      @mouseup.stop
      @pointerdown.stop
      @touchstart.stop
      @click.stop
      @contextmenu.prevent.stop
    >
      <button
        type="button"
        class="conversation-chat-context-item"
        @click.stop="startReply(contextMenuMessage)"
      >
        <v-icon size="20" color="#222">mdi-reply</v-icon>
        <span>{{ label("reply", "Reply") }}</span>
      </button>
      <button
        type="button"
        class="conversation-chat-context-item"
        @click.stop="copyMessage(contextMenuMessage)"
      >
        <v-icon size="20" color="#222">mdi-content-copy</v-icon>
        <span>{{ label("copy", "Copy") }}</span>
      </button>
      <button
        type="button"
        class="conversation-chat-context-item"
        @click.stop="startForward(contextMenuMessage)"
      >
        <v-icon size="20" color="#222">mdi-share</v-icon>
        <span>{{ label("forward", "Forward") }}</span>
      </button>
      <template v-if="contextMenuMessage.mine">
        <button
          type="button"
          class="conversation-chat-context-item"
          @click.stop="startEdit(contextMenuMessage)"
        >
          <v-icon size="20" color="#222">mdi-pencil-outline</v-icon>
          <span>{{ $t("edit") }}</span>
        </button>
        <div class="conversation-chat-context-divider" />
        <button
          type="button"
          class="conversation-chat-context-item is-danger"
          @click.stop="confirmDelete(contextMenuMessage)"
        >
          <v-icon size="20" color="#e53935">mdi-delete-outline</v-icon>
          <span>{{ $t("delete") }}</span>
        </button>
      </template>
    </div>
  </section>
</template>

<script>
export default {
  name: "ConversationChat",
  props: {
    conversation: {
      type: Object,
      default: null,
    },
    messages: {
      type: Array,
      default() {
        return [];
      },
    },
    members: {
      type: Array,
      default() {
        return [];
      },
    },
    forwardTargets: {
      type: Array,
      default() {
        return [];
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    sending: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    composerHidden: {
      type: Boolean,
      default: false,
    },
    stateBusy: {
      type: Boolean,
      default: false,
    },
    membersCount: {
      type: Number,
      default: 0,
    },
    membersOpen: {
      type: Boolean,
      default: false,
    },
    pending: {
      type: Boolean,
      default: false,
    },
    canCloseGroup: {
      type: Boolean,
      default: false,
    },
    canDeleteChat: {
      type: Boolean,
      default: false,
    },
    canLeaveGroup: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      draft: "",
      files: [],
      filePreviews: [],
      fileSizeError: "",
      _fileSizeErrorTimer: null,
      replyToMessage: null,
      editingMessageId: null,
      mentionUserIds: [],
      mentionOpen: false,
      mentionQuery: "",
      mentionIndex: 0,
      mentionStart: -1,
      forwardOpen: false,
      forwardMessage: null,
      contextMenuMessage: null,
      contextMenuX: 0,
      contextMenuY: 0,
      contextMenuAbsolute: false,
      _contextClickX: 0,
      _contextClickY: 0,
      highlightedMessageId: null,
      pendingScrollMessageId: null,
      _highlightTimer: null,
      _pendingScrollTimer: null,
    };
  },
  computed: {
    showHeaderMoreMenu() {
      return !!(
        this.canDeleteChat ||
        this.canCloseGroup ||
        this.canLeaveGroup
      );
    },
    showMembersButton() {
      var c = this.conversation;
      if (!c || this.pending) {
        return false;
      }
      if (c.type === "personal") {
        return false;
      }
      // Group/channel: only creator can manage members
      if (c.type === "group" || c.type === "channel") {
        return this.canCloseGroup;
      }
      // Ticket chats: keep members panel available
      return true;
    },
    deleteChatMenuLabel() {
      var c = this.conversation;
      if (c && c.type === "group") {
        return this.$t("conversation-delete-group");
      }
      if (c && c.type === "channel") {
        return this.$t("conversation-delete-channel");
      }
      return this.$t("conversation-delete-chat");
    },
    contextMenuStyle() {
      return {
        position: this.contextMenuAbsolute ? "absolute" : "fixed",
        left: this.contextMenuX + "px",
        top: this.contextMenuY + "px",
        zIndex: 10050,
      };
    },
    canSend() {
      if (this.disabled || this.composerHidden) {
        return false;
      }
      if (this.sending && this.editingMessageId != null) {
        return false;
      }
      var hasText = !!(this.draft && String(this.draft).trim());
      return hasText || this.files.length > 0;
    },
    showSender() {
      var c = this.conversation;
      if (!c) {
        return false;
      }
      // Channels: Telegram-style posts — no per-message sender
      if (c.type === "channel") {
        return false;
      }
      // Ticket chats may be multi-party; always show peer names there
      if (c.type === "ticket" || c.refType === "ticket") {
        return true;
      }
      return c.type !== "personal";
    },
    showPeerAvatars() {
      var c = this.conversation;
      if (!c) {
        return false;
      }
      // Channels: no avatars beside posts (like Telegram)
      return c.type !== "channel";
    },
    ticketDisplayTitle() {
      if (!this.conversation) {
        return "";
      }
      var title = String(this.conversation.title || "").trim();
      if (!title || this.conversation.ticketId == null) {
        return title;
      }
      var id = String(this.conversation.ticketId);
      var persian = this.toPersianDigits(id);
      if (
        title === id ||
        title === persian ||
        title === "#" + id ||
        title === "#" + persian
      ) {
        return "";
      }
      return title;
    },
    mentionCandidates() {
      var self = this;
      var q = String(this.mentionQuery || "")
        .trim()
        .toLowerCase();
      return (this.members || []).filter(function (item) {
        var name = self.memberDisplayName(item).toLowerCase();
        if (!name) {
          return false;
        }
        return !q || name.indexOf(q) > -1;
      }).slice(0, 8);
    },
  },
  watch: {
    conversation() {
      this.resetComposer();
      this.cancelForward();
      this.closeMessageMenu();
      if (this.pendingScrollMessageId == null) {
        this.scrollToBottom();
      }
    },
    messages() {
      if (this.pendingScrollMessageId != null) {
        this.scrollToMessage(this.pendingScrollMessageId);
        return;
      }
      this.scrollToBottom();
    },
    loading(val) {
      if (val) {
        return;
      }
      if (this.pendingScrollMessageId != null) {
        this.scrollToMessage(this.pendingScrollMessageId);
        return;
      }
      this.scrollToBottom();
    },
  },
  mounted() {
    this._onDocPointer = function (e) {
      if (!this.contextMenuMessage) {
        return;
      }
      var menu = this.$refs.contextMenu;
      if (menu && menu.contains(e.target)) {
        return;
      }
      this.closeMessageMenu();
    }.bind(this);
    this._onDocKeydown = function (e) {
      if (e.key === "Escape") {
        this.closeMessageMenu();
      }
    }.bind(this);
    this._onScrollCloseMenu = function () {
      if (this.contextMenuMessage) {
        this.closeMessageMenu();
      }
    }.bind(this);
    document.addEventListener("click", this._onDocPointer, true);
    document.addEventListener("contextmenu", this._onDocPointer, true);
    document.addEventListener("keydown", this._onDocKeydown, true);
    window.addEventListener("resize", this._onScrollCloseMenu, true);
  },
  beforeDestroy() {
    this.unbindContextMenuScroll();
    this.detachContextMenuFromBody();
    this.clearFileSizeError();
    this.revokePreviews(this.filePreviews);
    if (this._highlightTimer) {
      clearTimeout(this._highlightTimer);
      this._highlightTimer = null;
    }
    if (this._pendingScrollTimer) {
      clearTimeout(this._pendingScrollTimer);
      this._pendingScrollTimer = null;
    }
    if (this._onDocPointer) {
      document.removeEventListener("click", this._onDocPointer, true);
      document.removeEventListener("contextmenu", this._onDocPointer, true);
    }
    if (this._onDocKeydown) {
      document.removeEventListener("keydown", this._onDocKeydown, true);
    }
    if (this._onScrollCloseMenu) {
      window.removeEventListener("resize", this._onScrollCloseMenu, true);
    }
  },
  methods: {
    bindContextMenuScroll() {
      this.unbindContextMenuScroll();
      var area = this.$refs.messagesEl;
      if (area && this._onScrollCloseMenu) {
        area.addEventListener("scroll", this._onScrollCloseMenu, true);
        this._contextScrollEl = area;
      }
    },
    unbindContextMenuScroll() {
      if (this._contextScrollEl && this._onScrollCloseMenu) {
        this._contextScrollEl.removeEventListener(
          "scroll",
          this._onScrollCloseMenu,
          true
        );
      }
      this._contextScrollEl = null;
    },
    openMessageMenu(msg, event) {
      if (!msg || msg.system || msg.pending || msg.failed) {
        return;
      }
      var x = event && event.clientX != null ? event.clientX : 0;
      var y = event && event.clientY != null ? event.clientY : 0;
      this._contextClickX = x;
      this._contextClickY = y;
      this.contextMenuMessage = msg;
      this.contextMenuX = x;
      this.contextMenuY = y;
      var self = this;
      this.$nextTick(function () {
        self.ensureContextMenuInBody();
        self.positionContextMenu();
        self.bindContextMenuScroll();
      });
    },
    ensureContextMenuInBody() {
      var menu = this.$refs.contextMenu;
      if (!menu) {
        return;
      }
      var host = this.getContextMenuHost();
      if (menu.parentNode !== host) {
        host.appendChild(menu);
      }
      this.bindContextMenuEventGuard(menu);
    },
    getContextMenuHost() {
      var root = this.$el;
      if (root && root.closest) {
        var drawer =
          root.closest(".v-navigation-drawer") ||
          root.closest(".ticket-conversation-drawer");
        if (drawer) {
          return drawer;
        }
      }
      return document.body;
    },
    bindContextMenuEventGuard(menu) {
      if (!menu || menu._conversationMenuGuardBound) {
        return;
      }
      // Bubble-phase only: capture would block child button clicks
      var stop = function (e) {
        e.stopPropagation();
      };
      menu._conversationMenuStop = stop;
      ["mousedown", "mouseup", "click", "touchstart", "pointerdown"].forEach(
        function (type) {
          menu.addEventListener(type, stop, false);
        }
      );
      menu._conversationMenuGuardBound = true;
    },
    unbindContextMenuEventGuard(menu) {
      if (!menu || !menu._conversationMenuGuardBound) {
        return;
      }
      var stop = menu._conversationMenuStop;
      if (stop) {
        ["mousedown", "mouseup", "click", "touchstart", "pointerdown"].forEach(
          function (type) {
            menu.removeEventListener(type, stop, false);
          }
        );
      }
      menu._conversationMenuStop = null;
      menu._conversationMenuGuardBound = false;
    },
    detachContextMenuFromBody() {
      var menu = this.$refs.contextMenu;
      if (!menu) {
        return;
      }
      this.unbindContextMenuEventGuard(menu);
      if (menu.parentNode && menu.parentNode !== this.$el) {
        menu.parentNode.removeChild(menu);
      }
    },
    getContextMenuBounds() {
      var pad = 8;
      var host = this.getContextMenuHost();
      var hostRect =
        host && host.getBoundingClientRect
          ? host.getBoundingClientRect()
          : {
              left: 0,
              top: 0,
              right: window.innerWidth || 0,
              bottom: window.innerHeight || 0,
            };
      var vw = window.innerWidth || document.documentElement.clientWidth || 0;
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      var bounds = {
        left: Math.max(pad, hostRect.left + pad),
        top: Math.max(pad, hostRect.top + pad),
        right: Math.min(vw - pad, hostRect.right - pad),
        bottom: Math.min(vh - pad, hostRect.bottom - pad),
      };
      var area = this.$refs.messagesEl;
      if (!area) {
        return bounds;
      }
      var rect = area.getBoundingClientRect();
      return {
        left: Math.max(bounds.left, rect.left + pad),
        top: Math.max(bounds.top, rect.top + pad),
        right: Math.min(bounds.right, rect.right - pad),
        bottom: Math.min(bounds.bottom, rect.bottom - pad),
      };
    },
    positionContextMenu() {
      var menu = this.$refs.contextMenu;
      if (!menu) {
        return;
      }
      var mw = menu.offsetWidth || 168;
      var mh = menu.offsetHeight || 180;
      var gap = 12;
      var bounds = this.getContextMenuBounds();
      var clickX = this._contextClickX;
      var clickY = this._contextClickY;

      // Prefer opening to the left of the click so the bubble stays readable
      var x = clickX - mw - gap;
      if (x < bounds.left) {
        x = clickX + gap;
      }
      if (x + mw > bounds.right) {
        x = bounds.right - mw;
      }
      if (x < bounds.left) {
        x = bounds.left;
      }

      var y = clickY - 10;
      if (y + mh > bounds.bottom) {
        y = bounds.bottom - mh;
      }
      if (y < bounds.top) {
        y = bounds.top;
      }

      // If menu is hosted inside a drawer, use absolute coords relative to host
      var host = this.getContextMenuHost();
      if (host && host !== document.body) {
        var hostRect = host.getBoundingClientRect();
        x = x - hostRect.left;
        y = y - hostRect.top;
        this.contextMenuAbsolute = true;
      } else {
        this.contextMenuAbsolute = false;
      }

      this.contextMenuX = Math.round(x);
      this.contextMenuY = Math.round(y);
    },
    closeMessageMenu() {
      this.unbindContextMenuScroll();
      this.detachContextMenuFromBody();
      this.contextMenuMessage = null;
      this.contextMenuAbsolute = false;
    },
    label(key, fallback) {
      var map = {
        reply: "conversation-reply",
        forward: "conversation-forward",
        forwarded: "conversation-forwarded",
        forwardedFrom: "conversation-forwarded-from",
        copy: "conversation-copy",
        deleted: "deleted",
      };
      var i18nKey = map[key] || key;
      if (this.$te && this.$te(i18nKey)) {
        return this.$t(i18nKey);
      }
      if (this.$te && this.$te(key)) {
        return this.$t(key);
      }
      return fallback;
    },
    titleInitial(title) {
      return title ? String(title).charAt(0) : "?";
    },
    toPersianDigits(value) {
      if (value == null) {
        return "";
      }
      return String(value).replace(/\d/g, function (d) {
        return "۰۱۲۳۴۵۶۷۸۹"[d];
      });
    },
    messageReaders(msg) {
      if (!msg || !Array.isArray(msg.readers)) {
        return [];
      }
      return msg.readers.filter(function (reader) {
        return reader && reader.name;
      });
    },
    isImage(type) {
      return !!(type && String(type).indexOf("image") > -1);
    },
    isImageExt(value) {
      var raw = String(value || "").toLowerCase();
      if (!raw) {
        return false;
      }
      var clean = raw.indexOf(".") > -1 ? raw.slice(raw.lastIndexOf(".") + 1) : raw;
      clean = clean.split("?")[0];
      return (
        clean === "png" ||
        clean === "jpg" ||
        clean === "jpeg" ||
        clean === "gif" ||
        clean === "webp" ||
        clean === "bmp" ||
        clean === "svg"
      );
    },
    memberUserId(item) {
      if (!item) {
        return null;
      }
      if (item.user && item.user.id != null) {
        return item.user.id;
      }
      if (item.user_id != null) {
        return item.user_id;
      }
      return item.id != null ? item.id : null;
    },
    memberDisplayName(item) {
      if (!item) {
        return "";
      }
      var user = item.user || item;
      if (user.name) {
        return String(user.name);
      }
      var parts = [user.firstname, user.lastname].filter(Boolean);
      if (parts.length) {
        return parts.join(" ").trim();
      }
      if (item.user_id != null) {
        return "#" + item.user_id;
      }
      return "";
    },
    memberAvatarUrl(item) {
      var user = item && (item.user || item);
      if (!user) {
        return "";
      }
      var avatar = user.avatar;
      if (avatar && typeof avatar === "object") {
        return avatar.link || avatar.url || avatar.path || "";
      }
      if (typeof avatar === "string" && avatar) {
        return avatar;
      }
      var nested =
        user.avatar_url ||
        user.photo ||
        user.image ||
        user.profile_image ||
        null;
      if (nested && typeof nested === "object") {
        return nested.link || nested.url || nested.path || "";
      }
      return nested || "";
    },
    memberAvatarColor(item) {
      var user = item && (item.user || item);
      if (user && user.color) {
        return user.color;
      }
      var uid = this.memberUserId(item);
      var palette = [
        "#5B8DEF",
        "#4DB6AC",
        "#7E57C2",
        "#EF6C00",
        "#EC407A",
        "#26A69A",
        "#42A5F5",
        "#AB47BC",
      ];
      var seed = Number(uid) || 0;
      if (!seed) {
        var name = this.memberDisplayName(item);
        seed = String(name || "")
          .split("")
          .reduce(function (sum, ch) {
            return sum + ch.charCodeAt(0);
          }, 0);
      }
      return palette[Math.abs(seed) % palette.length];
    },
    triggerFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },
    onAttach(event) {
      var selected = Array.from(
        (event && event.target && event.target.files) || []
      );
      this.appendFiles(selected);
      if (event && event.target) {
        event.target.value = null;
      }
    },
    handlePaste(event) {
      var items = (event.clipboardData && event.clipboardData.items) || [];
      var files = [];
      for (var i = 0; i < items.length; i++) {
        var file = items[i].getAsFile && items[i].getAsFile();
        if (file) {
          files.push(file);
        }
      }
      if (files.length) {
        event.preventDefault();
        this.appendFiles(files);
      }
    },
    appendFiles(selected) {
      var self = this;
      var maxSize = 1 * 1024 * 1024;
      var rejected = false;
      selected.forEach(function (file) {
        if (file && file.size > maxSize) {
          rejected = true;
          return;
        }
        var exists = self.files.some(function (existing) {
          return existing.name === file.name && existing.type === file.type;
        });
        if (exists) {
          return;
        }
        self.files.push(file);
        self.filePreviews.push({
          name: file.name,
          type: file.type,
          preview: URL.createObjectURL(file),
          file: file,
        });
      });
      if (rejected) {
        this.showChatFileSizeError();
      }
    },
    showChatFileSizeError() {
      var message =
        this.$te && this.$te("File size must be less than 1 MB")
          ? this.$t("File size must be less than 1 MB")
          : "حجم فایل نباید بیشتر از 1 MB باشد";
      this.fileSizeError = message;
      if (this._fileSizeErrorTimer) {
        clearTimeout(this._fileSizeErrorTimer);
      }
      var self = this;
      this._fileSizeErrorTimer = setTimeout(function () {
        self.clearFileSizeError();
      }, 4500);
    },
    clearFileSizeError() {
      this.fileSizeError = "";
      if (this._fileSizeErrorTimer) {
        clearTimeout(this._fileSizeErrorTimer);
        this._fileSizeErrorTimer = null;
      }
    },
    removeFile(index) {
      var preview = this.filePreviews[index];
      if (preview && preview.preview) {
        URL.revokeObjectURL(preview.preview);
      }
      this.filePreviews.splice(index, 1);
      this.files.splice(index, 1);
    },
    revokePreviews(list) {
      (list || []).forEach(function (item) {
        if (item && item.preview) {
          URL.revokeObjectURL(item.preview);
        }
      });
    },
    resetComposer() {
      this.draft = "";
      this.revokePreviews(this.filePreviews);
      this.files = [];
      this.filePreviews = [];
      this.replyToMessage = null;
      this.editingMessageId = null;
      this.mentionUserIds = [];
      this.closeMention();
    },
    copyMessage(msg) {
      if (!msg || msg.system) {
        return;
      }
      var text = msg.text != null ? String(msg.text) : "";
      if (!text && msg.attachments && msg.attachments.length) {
        text = msg.attachments
          .map(function (file) {
            return (file && (file.preview || file.name)) || "";
          })
          .filter(Boolean)
          .join("\n");
      }
      this.closeMessageMenu();
      if (!text) {
        return;
      }
      var self = this;
      var done = function () {
        if (self.$toast && typeof self.$toast.success === "function") {
          self.$toast.success(
            self.$te && self.$te("copied") ? self.$t("copied") : "Copied"
          );
        }
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(function () {
          self.copyMessageFallback(text);
          done();
        });
        return;
      }
      this.copyMessageFallback(text);
      done();
    },
    copyMessageFallback(text) {
      var area = document.createElement("textarea");
      area.value = text;
      area.setAttribute("readonly", "");
      area.style.position = "fixed";
      area.style.left = "-9999px";
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand("copy");
      } catch (e) {
        // ignore
      }
      document.body.removeChild(area);
    },
    startReply(msg) {
      if (!msg || msg.system) {
        return;
      }
      this.closeMessageMenu();
      this.editingMessageId = null;
      this.replyToMessage = {
        id: msg.id,
        text: msg.text || "",
        sender: msg.sender || "",
      };
      this.$nextTick(function () {
        if (this.$refs.draftInput) {
          this.$refs.draftInput.focus();
        }
      }.bind(this));
    },
    cancelReply() {
      this.replyToMessage = null;
    },
    startEdit(msg) {
      if (!msg || !msg.mine) {
        return;
      }
      this.closeMessageMenu();
      this.replyToMessage = null;
      this.revokePreviews(this.filePreviews);
      this.files = [];
      this.filePreviews = [];
      this.editingMessageId = msg.id;
      this.draft = msg.text || "";
      this.mentionUserIds = (msg.mentions || [])
        .map(function (m) {
          return m.userId;
        })
        .filter(function (id) {
          return id != null;
        });
      this.closeMention();
      this.$nextTick(function () {
        if (this.$refs.draftInput) {
          this.$refs.draftInput.focus();
        }
      }.bind(this));
    },
    cancelEdit() {
      this.editingMessageId = null;
      this.draft = "";
      this.mentionUserIds = [];
      this.revokePreviews(this.filePreviews);
      this.files = [];
      this.filePreviews = [];
      this.closeMention();
    },
    confirmDelete(msg) {
      if (!msg) {
        return;
      }
      this.closeMessageMenu();
      this.$emit("delete-message", msg);
    },
    startForward(msg) {
      if (!msg || msg.system) {
        return;
      }
      this.closeMessageMenu();
      this.forwardMessage = msg;
      this.forwardOpen = true;
    },
    cancelForward() {
      this.forwardOpen = false;
      this.forwardMessage = null;
    },
    pickForwardTarget(target) {
      if (!target || !this.forwardMessage) {
        return;
      }
      this.$emit("forward-message", {
        messageId: this.forwardMessage.id,
        chatId: target.id,
        caption: "",
      });
      this.cancelForward();
    },
    onDraftInput() {
      var value = String(this.draft || "");
      var caret = value.length;
      if (this.$refs.draftInput && this.$refs.draftInput.selectionStart != null) {
        caret = this.$refs.draftInput.selectionStart;
      }
      var before = value.slice(0, caret);
      var match = before.match(/(^|\s)@([^\s@]*)$/);
      if (match) {
        this.mentionOpen = true;
        this.mentionQuery = match[2] || "";
        this.mentionStart = before.lastIndexOf("@");
        this.mentionIndex = 0;
      } else {
        this.closeMention();
      }
    },
    closeMention() {
      this.mentionOpen = false;
      this.mentionQuery = "";
      this.mentionIndex = 0;
      this.mentionStart = -1;
    },
    mentionMove(delta) {
      if (!this.mentionOpen || !this.mentionCandidates.length) {
        return;
      }
      var len = this.mentionCandidates.length;
      this.mentionIndex = (this.mentionIndex + delta + len) % len;
    },
    selectMention(item) {
      var name = this.memberDisplayName(item);
      var userId = this.memberUserId(item);
      if (!name || this.mentionStart < 0) {
        this.closeMention();
        return;
      }
      var value = String(this.draft || "");
      var before = value.slice(0, this.mentionStart);
      var afterMatch = value.slice(this.mentionStart).match(/^@[^\s@]*/);
      var rest = afterMatch
        ? value.slice(this.mentionStart + afterMatch[0].length)
        : value.slice(this.mentionStart);
      this.draft = before + "@" + name + " " + rest.replace(/^\s*/, "");
      if (
        userId != null &&
        this.mentionUserIds.indexOf(userId) === -1
      ) {
        this.mentionUserIds.push(userId);
      }
      this.closeMention();
      this.$nextTick(function () {
        if (this.$refs.draftInput) {
          this.$refs.draftInput.focus();
        }
      }.bind(this));
    },
    send() {
      if (!this.canSend) {
        return;
      }
      if (
        this.mentionOpen &&
        this.mentionCandidates.length &&
        this.mentionCandidates[this.mentionIndex]
      ) {
        this.selectMention(this.mentionCandidates[this.mentionIndex]);
        return;
      }
      var text = this.draft ? String(this.draft).trim() : "";
      var mentionUserIds = this.mentionUserIds.slice();

      if (this.editingMessageId != null) {
        this.$emit("edit-message", {
          id: this.editingMessageId,
          text: text,
          files: this.files.slice(),
          mentionUserIds: mentionUserIds,
        });
        this.editingMessageId = null;
        this.draft = "";
        this.revokePreviews(this.filePreviews);
        this.files = [];
        this.filePreviews = [];
        this.mentionUserIds = [];
        this.closeMention();
        return;
      }

      var attachments = this.filePreviews.map(function (item) {
        return {
          name: item.name,
          type: item.type,
          preview: item.preview,
        };
      });
      this.$emit("send", {
        text: text,
        files: this.files.slice(),
        attachments: attachments,
        replyToMessageId: this.replyToMessage
          ? this.replyToMessage.id
          : null,
        mentionUserIds: mentionUserIds,
      });
      this.draft = "";
      this.files = [];
      this.filePreviews = [];
      this.replyToMessage = null;
      this.mentionUserIds = [];
      this.closeMention();
    },
    scrollToBottom() {
      var self = this;
      this.$nextTick(function () {
        var el = self.$refs.messagesEl;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    },
    scrollToMessage(messageId, attempt) {
      if (messageId == null) {
        return;
      }
      this.pendingScrollMessageId = messageId;
      var tries = attempt == null ? 0 : attempt;
      var done = this.scrollToReplyTarget({
        id: messageId,
        deleted: false,
      });
      if (done) {
        this.pendingScrollMessageId = null;
        if (this._pendingScrollTimer) {
          clearTimeout(this._pendingScrollTimer);
          this._pendingScrollTimer = null;
        }
        return;
      }
      if (tries >= 12) {
        this.pendingScrollMessageId = null;
        return;
      }
      var self = this;
      if (this._pendingScrollTimer) {
        clearTimeout(this._pendingScrollTimer);
      }
      this._pendingScrollTimer = setTimeout(function () {
        self._pendingScrollTimer = null;
        self.scrollToMessage(messageId, tries + 1);
      }, 80);
    },
    scrollToReplyTarget(replyTo) {
      if (!replyTo || replyTo.id == null || replyTo.deleted) {
        return false;
      }
      var targetId = replyTo.id;
      var container = this.$refs.messagesEl;
      if (!container) {
        return false;
      }
      var rows = container.querySelectorAll("[data-message-id]");
      var target = null;
      for (var i = 0; i < rows.length; i++) {
        if (String(rows[i].getAttribute("data-message-id")) === String(targetId)) {
          target = rows[i];
          break;
        }
      }
      if (!target) {
        return false;
      }
      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      if (this._highlightTimer) {
        clearTimeout(this._highlightTimer);
      }
      this.highlightedMessageId = targetId;
      var self = this;
      this._highlightTimer = setTimeout(function () {
        self.highlightedMessageId = null;
        self._highlightTimer = null;
      }, 1600);
      return true;
    },
    escapeRegExp(value) {
      return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    },
    messageParts(msg) {
      var text = msg && msg.text != null ? String(msg.text) : "";
      var mentions = (msg && msg.mentions) || [];
      var usable = mentions.filter(function (m) {
        return m && m.userId != null && m.name;
      });
      if (!text || !usable.length) {
        return [{ type: "text", text: text }];
      }
      var sorted = usable.slice().sort(function (a, b) {
        return String(b.name).length - String(a.name).length;
      });
      var pattern = sorted
        .map(
          function (m) {
            return "@" + this.escapeRegExp(m.name);
          }.bind(this)
        )
        .join("|");
      if (!pattern) {
        return [{ type: "text", text: text }];
      }
      var re = new RegExp("(" + pattern + ")", "g");
      var parts = [];
      var lastIndex = 0;
      var match;
      while ((match = re.exec(text)) !== null) {
        if (match.index > lastIndex) {
          parts.push({
            type: "text",
            text: text.slice(lastIndex, match.index),
          });
        }
        var token = match[0];
        var name = token.slice(1);
        var found = null;
        for (var i = 0; i < sorted.length; i++) {
          if (String(sorted[i].name) === name) {
            found = sorted[i];
            break;
          }
        }
        if (found) {
          parts.push({
            type: "mention",
            text: token,
            userId: found.userId,
            name: found.name,
          });
        } else {
          parts.push({ type: "text", text: token });
        }
        lastIndex = match.index + token.length;
      }
      if (lastIndex < text.length) {
        parts.push({ type: "text", text: text.slice(lastIndex) });
      }
      return parts.length ? parts : [{ type: "text", text: text }];
    },
    onMentionClick(part) {
      if (!part || part.userId == null) {
        return;
      }
      this.$emit("open-user-chat", {
        userId: part.userId,
        name: part.name || "",
      });
    },
    onSenderClick(msg) {
      if (!msg || msg.mine || msg.userId == null) {
        return;
      }
      this.$emit("open-user-chat", {
        userId: msg.userId,
        name: this.displaySender(msg) || msg.sender || "",
      });
    },
    displaySender(msg) {
      if (!msg || msg.mine) {
        return "";
      }
      var name = String(msg.sender || "").trim();
      if (name && name !== "?") {
        return name;
      }
      if (msg.userId == null || !Array.isArray(this.members)) {
        return name;
      }
      for (var i = 0; i < this.members.length; i++) {
        var item = this.members[i];
        var uid = this.memberUserId(item);
        if (uid != null && Number(uid) === Number(msg.userId)) {
          var memberName = this.memberDisplayName(item);
          if (memberName) {
            return memberName;
          }
        }
      }
      return name;
    },
    onForwardedSenderClick(forwardedFrom) {
      if (!forwardedFrom || forwardedFrom.userId == null) {
        return;
      }
      this.$emit("open-user-chat", {
        userId: forwardedFrom.userId,
        name: forwardedFrom.sender || "",
      });
    },
    initialsOf(name) {
      var value = String(name || "").trim();
      if (!value) {
        return "?";
      }
      var parts = value.split(/\s+/).filter(Boolean);
      if (parts.length === 1) {
        return parts[0].slice(0, 2);
      }
      return parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
    },
    shouldShowPeerAvatar(msg, index) {
      if (!this.showPeerAvatars) {
        return false;
      }
      if (!msg || msg.mine || msg.system) {
        return false;
      }
      var list = this.messages || [];
      var next = list[index + 1];
      if (!next || next.mine || next.system) {
        return true;
      }
      if (msg.userId == null || next.userId == null) {
        return true;
      }
      return String(msg.userId) !== String(next.userId);
    },
  },
};
</script>

<style scoped>
.conversation-chat {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  direction: rtl;
  position: relative;
}
.conversation-chat-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  background: #fff;
  position: relative;
  z-index: 6;
  overflow: visible;
}
.conversation-chat-header-main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.conversation-chat-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  flex: 0 0 auto;
  overflow: hidden;
}
.conversation-chat-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.conversation-chat-header-text {
  min-width: 0;
}
.conversation-chat-title {
  font-size: 14px;
  font-weight: 600;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 8px;
}
.conversation-chat-state {
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #6b7280;
}
.conversation-chat-state.is-closed {
  background: #fef3c7;
  color: #b45309;
}
.conversation-chat-subtitle {
  font-size: 12px;
  color: #8a8a8a;
  margin-top: 2px;
}
.conversation-chat-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
}
.conversation-chat-actions > .v-tooltip,
.conversation-chat-members-wrap > .v-tooltip {
  display: inline-flex;
}
.conversation-chat-members-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.conversation-chat-action {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.conversation-chat-action:hover:not(:disabled) {
  background: #f3f4f6;
}
.conversation-chat-action.is-active {
  background: #e8f1ff;
}
.conversation-chat-action.is-primary:hover:not(:disabled) {
  background: #e8f1ff;
}
.conversation-chat-action.is-danger:hover:not(:disabled) {
  background: #fef2f2;
}
.conversation-chat-action:disabled {
  cursor: default;
  opacity: 0.6;
}
.conversation-chat-header-menu {
  border-radius: 10px !important;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12) !important;
  overflow: hidden;
}
.conversation-chat-header-menu-list {
  min-width: 168px;
  padding: 4px 0 !important;
}
.conversation-chat-header-menu-icon {
  margin-right: 0 !important;
  margin-left: 8px !important;
  min-width: 28px !important;
}
.conversation-chat-header-menu-danger .v-list-item__title {
  color: #ef4444;
}
.conversation-chat-empty {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #9a9a9a;
  background: #f7f8fa;
}
.conversation-chat-empty p {
  margin: 0;
  font-size: 14px;
}
.conversation-chat-body {
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
}
.conversation-chat-messages {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  /* Bubble sides: physical left/right like Telegram (independent of page RTL) */
  direction: ltr;
  background:
    radial-gradient(circle at 20% 20%, rgba(0, 102, 255, 0.04), transparent 40%),
    radial-gradient(circle at 80% 0%, rgba(0, 0, 0, 0.03), transparent 35%),
    #f5f6f8;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.18) transparent;
}
.conversation-chat-messages::-webkit-scrollbar {
  width: 4px;
}
.conversation-chat-messages::-webkit-scrollbar-track {
  background: transparent;
}
.conversation-chat-messages::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.16);
  border-radius: 999px;
}
.conversation-chat-messages::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.28);
}
.conversation-chat-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
}
.conversation-chat-closed {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 14px;
  font-size: 12px;
  color: #b45309;
  background: #fffbeb;
  border-bottom: 1px solid #fde68a;
}
.conversation-chat-reopen-btn {
  border: none;
  background: #0066ff;
  color: #fff;
  font-size: 12px;
  font-family: inherit;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
}
.conversation-chat-reopen-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.conversation-chat-row {
  display: flex;
  margin-bottom: 8px;
  width: 100%;
  align-items: flex-end;
  gap: 8px;
}
.conversation-chat-row.is-highlighted .conversation-chat-bubble {
  animation: conversation-chat-flash 1.4s ease;
}
@keyframes conversation-chat-flash {
  0%,
  100% {
    box-shadow: 0 1px 1px rgba(15, 23, 42, 0.04);
  }
  25%,
  55% {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.35);
  }
}
.conversation-chat-row.is-mine {
  justify-content: flex-end;
}
.conversation-chat-row:not(.is-mine):not(.is-system) {
  justify-content: flex-start;
}
.conversation-chat-row.is-system {
  justify-content: center;
}
.conversation-chat-msg-avatar-wrap {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  align-self: flex-end;
}
.conversation-chat-msg-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  user-select: none;
}
.conversation-chat-msg-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.conversation-chat-system {
  font-size: 12px;
  color: #6b7280;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 4px 12px;
  direction: rtl;
}
.conversation-chat-bubble {
  position: relative;
  max-width: min(78%, 420px);
  padding: 8px 12px 6px;
  border-radius: 16px;
  background: #fff;
  color: #111;
  box-shadow: 0 1px 1px rgba(15, 23, 42, 0.04);
  direction: rtl;
  text-align: right;
  overflow: visible;
}
/* Own messages: soft reply-blue */
.conversation-chat-row.is-mine .conversation-chat-bubble {
  background: #eaf2ff;
  color: #111;
  border-bottom-right-radius: 4px;
}
/* Others: white, left side */
.conversation-chat-row:not(.is-mine) .conversation-chat-bubble {
  background: #fff;
  color: #111;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-bottom-left-radius: 4px;
}
.conversation-chat-context-menu {
  z-index: 10050;
  min-width: 168px;
  padding: 6px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow:
    0 8px 28px rgba(15, 23, 42, 0.18),
    0 0 0 1px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  gap: 1px;
  direction: rtl;
  pointer-events: auto;
}
.conversation-chat-context-item {
  width: 100%;
  min-height: 40px;
  border: none;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  padding: 8px 12px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  color: #1f1f1f;
  line-height: 1.2;
  white-space: nowrap;
}
.conversation-chat-context-item:hover {
  background: rgba(0, 0, 0, 0.05);
}
.conversation-chat-context-item.is-danger {
  color: #e53935;
}
.conversation-chat-context-item.is-danger:hover {
  background: rgba(229, 57, 53, 0.08);
}
.conversation-chat-context-divider {
  height: 1px;
  margin: 4px 8px;
  background: rgba(15, 23, 42, 0.08);
}
.conversation-chat-sender {
  font-size: 11px;
  font-weight: 600;
  color: #0066ff;
  margin-bottom: 2px;
  border: none;
  background: transparent;
  padding: 0;
  font-family: inherit;
  text-align: inherit;
  display: inline-block;
  max-width: 100%;
}
.conversation-chat-sender.is-clickable {
  cursor: pointer;
}
.conversation-chat-sender.is-clickable:hover:not(:disabled) {
  opacity: 0.85;
}
.conversation-chat-sender:disabled {
  cursor: default;
  opacity: 0.85;
}
.conversation-chat-row.is-mine .conversation-chat-sender {
  color: #3b82f6;
}
.conversation-chat-forwarded {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  margin-bottom: 4px;
  padding-bottom: 0;
  border-bottom: none;
}
.conversation-chat-forwarded-label {
  font-size: 11px;
  font-weight: 500;
  color: #3b82f6;
  line-height: 1.15;
}
.conversation-chat-row.is-mine .conversation-chat-forwarded-label {
  color: #3b82f6;
}
.conversation-chat-forwarded-sender {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  border: none;
  background: transparent;
  padding: 0;
  font-family: inherit;
  text-align: inherit;
  cursor: default;
}
.conversation-chat-forwarded-sender.is-clickable {
  cursor: pointer;
}
.conversation-chat-forwarded-sender.is-clickable:hover .conversation-chat-forwarded-name {
  opacity: 0.85;
}
.conversation-chat-forwarded-sender:disabled {
  cursor: default;
}
.conversation-chat-forwarded-avatar {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(59, 130, 246, 0.16);
  color: #2563eb;
  font-size: 9px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
.conversation-chat-row.is-mine .conversation-chat-forwarded-avatar {
  background: rgba(59, 130, 246, 0.18);
  color: #2563eb;
}
.conversation-chat-forwarded-name {
  font-size: 11.5px;
  font-weight: 600;
  color: #2563eb;
  line-height: 1.15;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  text-decoration: none;
}
.conversation-chat-row.is-mine .conversation-chat-forwarded-name {
  color: #2563eb;
}
.conversation-chat-mention-link {
  display: inline;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  color: #2563eb;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}
.conversation-chat-mention-link:hover {
  text-decoration: none;
  opacity: 0.85;
}
.conversation-chat-row.is-mine .conversation-chat-mention-link {
  color: #2563eb;
  text-decoration: none;
}
.conversation-chat-quote {
  border-right: 3px solid #3b82f6;
  background: rgba(59, 130, 246, 0.08);
  border-radius: 6px;
  padding: 6px 8px;
  margin-bottom: 6px;
  font-size: 12px;
}
.conversation-chat-quote.is-clickable {
  cursor: pointer;
}
.conversation-chat-quote.is-clickable:hover {
  background: rgba(59, 130, 246, 0.14);
}
.conversation-chat-row.is-mine .conversation-chat-quote {
  border-right-color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
}
.conversation-chat-row.is-mine .conversation-chat-quote.is-clickable:hover {
  background: rgba(59, 130, 246, 0.16);
}
.conversation-chat-quote.is-deleted {
  opacity: 0.7;
  font-style: italic;
}
.conversation-chat-quote-sender {
  font-weight: 600;
  font-size: 11px;
  color: #3b82f6;
  margin-bottom: 2px;
}
.conversation-chat-row.is-mine .conversation-chat-quote-sender {
  color: #3b82f6;
}
.conversation-chat-quote-text {
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
  opacity: 0.9;
}
.conversation-chat-msg-files {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 6px;
}
.conversation-chat-msg-img-link {
  display: inline-block;
  line-height: 0;
  border-radius: 10px;
  overflow: hidden;
}
.conversation-chat-msg-img {
  max-width: 220px;
  max-height: 160px;
  border-radius: 10px;
  display: block;
  object-fit: cover;
}
.conversation-chat-msg-doc {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: inherit;
  text-decoration: none;
  max-width: 240px;
}
a.conversation-chat-msg-doc:hover span {
  text-decoration: underline;
}
.conversation-chat-msg-doc span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.conversation-chat-text {
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}
.conversation-chat-meta {
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
}
.conversation-chat-row.is-mine .conversation-chat-meta {
  color: #7a93b8;
}
.conversation-chat-row:not(.is-mine) .conversation-chat-meta {
  color: #9aa0a6;
}
.conversation-chat-meta > .v-tooltip {
  display: inline-flex;
}
.conversation-chat-edited {
  font-size: 10px;
  opacity: 0.65;
  font-style: italic;
}
.conversation-chat-time {
  font-size: 10px;
  opacity: 0.7;
}
.conversation-chat-checks {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}
.conversation-chat-readers-tip {
  text-align: right;
  direction: rtl;
  max-width: 240px;
  padding: 2px 0;
}
.conversation-chat-readers-tip-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 0;
}
.conversation-chat-readers-tip-avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  overflow: hidden;
  flex: 0 0 auto;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
.conversation-chat-readers-tip-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.conversation-chat-readers-tip-name {
  font-size: 12px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.conversation-chat-footer {
  flex: 0 0 auto;
  border-top: 1px solid #eee;
  background: #fff;
}
.conversation-chat-file-error {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 14px 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fff8eb;
  border: 1px solid #fde68a;
  border-right: 3px solid #FFAF2E;
  direction: rtl;
}
.conversation-chat-file-error-text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  font-weight: 500;
  color: #92400e;
  line-height: 1.4;
}
.conversation-chat-file-error > button {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.conversation-chat-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px 0;
  max-height: 120px;
  overflow-y: auto;
}
.conversation-chat-attachment {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.conversation-chat-attachment-img {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid #e5e5e5;
}
.conversation-chat-attachment-doc {
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 180px;
  padding: 8px 12px;
  border-radius: 10px;
  background: #f3f6fb;
  border: 1px solid #e5e5e5;
  font-size: 12px;
  color: #333;
}
.conversation-chat-attachment-doc span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.conversation-chat-attachment-remove {
  position: absolute;
  top: -6px;
  left: -6px;
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 50%;
  background: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.conversation-chat-composer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 14px 10px;
}
.conversation-chat-reply-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #f3f6fb;
  border-right: 3px solid #0066ff;
}
.conversation-chat-reply-bar.is-edit {
  border-right-color: #FFAF2E;
  background: #fff8eb;
}
.conversation-chat-reply-bar-body {
  flex: 1 1 auto;
  min-width: 0;
}
.conversation-chat-reply-bar-label {
  font-size: 11px;
  font-weight: 600;
  color: #0066ff;
}
.conversation-chat-reply-bar.is-edit .conversation-chat-reply-bar-label {
  color: #b45309;
}
.conversation-chat-reply-bar-text {
  font-size: 12px;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.conversation-chat-reply-bar > button {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  flex: 0 0 auto;
}
.conversation-chat-input-shell {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 36px;
  padding: 2px 4px 2px 6px;
  border: 1px solid #e2e5ea;
  border-radius: 8px;
  background: #fff;
  box-shadow: none;
  transition: border-color 0.15s ease;
}
.conversation-chat-input-shell:focus-within {
  border-color: #0066ff;
  box-shadow: none;
}
.conversation-chat-attach {
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  padding: 0;
  order: 3;
}
.conversation-chat-attach:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.05);
}
.conversation-chat-attach:disabled {
  opacity: 0.4;
  cursor: default;
}
.conversation-chat-input-wrap {
  flex: 1 1 auto;
  min-width: 0;
  position: relative;
  order: 2;
}
.conversation-chat-input {
  width: 100%;
  min-height: 28px;
  max-height: 120px;
  resize: none;
  border: none;
  outline: none;
  background: transparent;
  padding: 4px 4px;
  font-size: 13px;
  line-height: 1.4;
  font-family: inherit;
  color: #222;
}
.conversation-chat-input::placeholder {
  color: #9aa0a6;
}
.conversation-chat-mention-dropdown {
  position: absolute;
  bottom: calc(100% + 6px);
  right: 0;
  left: 0;
  max-height: 180px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  z-index: 8;
}
.conversation-chat-mention-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: right;
  border: none;
  background: transparent;
  padding: 8px 12px;
  font-size: 13px;
  font-family: inherit;
  color: #222;
  cursor: pointer;
}
.conversation-chat-mention-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
}
.conversation-chat-mention-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.conversation-chat-mention-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.conversation-chat-mention-item:hover,
.conversation-chat-mention-item.is-active {
  background: #e8f1ff;
  color: #0066ff;
}
.conversation-chat-send {
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: #e8ebf0;
  cursor: pointer;
  padding: 0;
  box-shadow: none;
  opacity: 1;
  transition: background 0.15s ease;
  order: 1;
}
.conversation-chat-send:disabled {
  cursor: default;
}
.conversation-chat-send.is-ready {
  background: #0066ff;
}
.conversation-chat-send.is-ready:hover {
  background: #0052cc;
}
.conversation-chat-forward-overlay {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  padding: 16px;
}
.conversation-chat-forward-panel {
  width: min(360px, 100%);
  max-height: min(420px, 80%);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
}
.conversation-chat-forward-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #eee;
  font-size: 14px;
  font-weight: 600;
}
.conversation-chat-forward-head button {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
}
.conversation-chat-forward-empty {
  padding: 24px 14px;
  text-align: center;
  color: #8a8a8a;
  font-size: 13px;
}
.conversation-chat-forward-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  border: none;
  background: transparent;
  padding: 10px 14px;
  cursor: pointer;
  text-align: right;
  font-family: inherit;
}
.conversation-chat-forward-item:hover {
  background: #f3f6fb;
}
.conversation-chat-forward-item + .conversation-chat-forward-item {
  border-top: 1px solid #f3f4f6;
}
.conversation-chat-forward-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex: 0 0 auto;
}
.conversation-chat-forward-meta {
  min-width: 0;
  flex: 1 1 auto;
}
.conversation-chat-forward-title {
  font-size: 13px;
  font-weight: 600;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.conversation-chat-forward-type {
  font-size: 11px;
  color: #8a8a8a;
  margin-top: 1px;
}
</style>
